// The week on a wide screen (wireframe 24): seven day columns with every
// meal showing, and the picker as a panel beside them that stays open.
// Click a meal to select it, then pick from the panel; after a pick the
// next empty dinner is selected. Drag a meal onto another day or meal to
// move it there (the two swap if both are planned); the panel's "Move to"
// does the same from the keyboard. A day's heading opens the day view.
import { useState, type DragEvent, type ReactNode } from "react";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { COLORS, DAYS, FONT_SIZE, PLANNABLE_DISH_TYPES, RADIUS, SPACE } from "../../lib/constants";
import { orderedDays } from "../../lib/utils";
import { normalizeDishes } from "../../lib/planUtils";
import { mealDate } from "../../lib/planScaling";
import { budgetStatus, dishLines, recentRecipeIds, type DishLine } from "../../lib/planView";
import type { Day, DishType, MealSlot } from "../../lib/types";
import { smallLabel, supporting } from "../recipeStyles";
import { PickerPanel, type PickerRequest } from "./PickerSheet";
import { WeekExtras } from "./WeekOverview";
import { inputStyle, textButton } from "./planStyles";

const SLOTS: MealSlot[] = ["dinner", "lunch", "breakfast"];
const SLOT_TITLES: Record<MealSlot, string> = { dinner: "Dinner", lunch: "Lunch", breakfast: "Breakfast" };
const DRAG_TYPE = "application/x-weekly-kitchen-meal";

interface Selection {
  day: Day;
  slot: MealSlot;
}

const sameSel = (a: Selection | null, b: Selection) => !!a && a.day === b.day && a.slot === b.slot;

export function DesktopWeek({ onOpenDay }: { onOpenDay: (day: Day) => void }) {
  const mp = useMealPlanner();
  const days = orderedDays(mp.planDefaults.weekStartDay) as Day[];
  const dishesAt = (s: Selection) => normalizeDishes(mp.weekData.days[s.day]?.[s.slot]) as Partial<Record<DishType, string>>;
  const isEmpty = (s: Selection) => !Object.values(dishesAt(s)).some(Boolean);

  // Start on the first empty dinner from today on (or from the week's start).
  const firstEmptyDinner = (after?: Day): Selection | null => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startIdx = after ? days.indexOf(after) + 1 : 0;
    const day = days.slice(startIdx).find((d) => (after || mealDate(mp.weekStart, d) >= today) && isEmpty({ day: d, slot: "dinner" }));
    return day ? { day, slot: "dinner" } : null;
  };
  const [selected, setSelected] = useState<Selection | null>(() => firstEmptyDinner());
  // Bumped on every selection so the panel starts fresh (tab, dish type).
  const [selectionId, setSelectionId] = useState(0);
  const select = (s: Selection | null) => {
    setSelected(s);
    setSelectionId((n) => n + 1);
  };
  const [dragOver, setDragOver] = useState<Selection | null>(null);

  const now = new Date();
  const recent = recentRecipeIds(mp.plansByWeek, mp.weekStart);

  let request: PickerRequest | null = null;
  if (selected) {
    const dishes = dishesAt(selected);
    const freeTypes = PLANNABLE_DISH_TYPES.filter((dt) => !dishes[dt]);
    const mainIsLeftover = !!mp.weekData.leftovers?.[`${selected.day}|${selected.slot}|entree`];
    // A planned meal opens on its Main (to change it) with the free dish
    // types beside it (to add a side); an empty one on its Main alone.
    request = dishes.entree
      ? { tab: mainIsLeftover ? "leftovers" : "recipes", dishType: "entree", typeChoices: ["entree", ...freeTypes] }
      : { tab: "recipes", dishType: "entree", typeChoices: ["entree"] };
  }

  const onDrop = (to: Selection) => (e: DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    const raw = e.dataTransfer.getData(DRAG_TYPE);
    if (!raw) return;
    const from = JSON.parse(raw) as Selection;
    mp.moveMeal(from, to);
    if (sameSel(selected, from) || sameSel(selected, to)) select(to);
  };

  return (
    <div className="mp-sans" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: SPACE.xxl, alignItems: "start" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.x3, minWidth: 0 }}>
        {/* One grid, row by row (the days, then dinners, lunches, breakfasts),
            so each meal lines up across the week. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: SPACE.sm }}>
          {days.map((day) => {
            const date = mealDate(mp.weekStart, day);
            const isToday = date.toDateString() === now.toDateString();
            const budget = budgetStatus(mp.weekData, day, mp.recipeById);
            return (
              <button
                key={day}
                type="button"
                onClick={() => onOpenDay(day)}
                aria-label={`Open ${date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${budget?.over ? `, ${budget.text} its time budget` : ""}`}
                aria-current={isToday ? "date" : undefined}
                style={{ ...textButton, flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-end", color: isToday ? COLORS.accent : COLORS.ink, borderBottom: `3px solid ${isToday ? COLORS.accent : COLORS.line}`, paddingBottom: SPACE.xs, textAlign: "left" }}
              >
                <span style={{ fontSize: FONT_SIZE.sm }}>{isToday ? "Today" : day.slice(0, 3)}</span>
                <span style={{ fontSize: FONT_SIZE.h3, fontWeight: 700, lineHeight: 1 }}>{date.getDate()}</span>
                {budget?.over && <span style={{ fontSize: FONT_SIZE.xxs, fontWeight: 700, color: COLORS.caution }}>{budget.text}</span>}
              </button>
            );
          })}
          {SLOTS.map((slot) =>
            days.map((day) => {
              const here = { day, slot };
              const lines = dishLines(mp.plansByWeek, mp.weekStart, day, slot, mp.recipeById, now, recent);
              return (
                <MealCell
                  key={`${slot}-${day}`}
                  slot={slot}
                  day={day}
                  lines={lines}
                  selected={sameSel(selected, here)}
                  dropping={sameSel(dragOver, here)}
                  onSelect={() => select(here)}
                  onDragOver={(e) => {
                    if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
                    e.preventDefault();
                    if (!sameSel(dragOver, here)) setDragOver(here);
                  }}
                  onDragLeave={() => setDragOver((d) => (sameSel(d, here) ? null : d))}
                  onDrop={onDrop(here)}
                />
              );
            })
          )}
        </div>
        <p style={{ ...supporting, margin: 0 }}>Every meal of every day shows here. Click one to choose for it in the panel. Drag a meal to another day to move it.</p>
        <div style={{ maxWidth: 720 }}>
          <WeekExtras />
        </div>
      </div>

      <div style={{ position: "sticky", top: SPACE.xxl, height: "calc(100vh - 2 * var(--space-xxl))", minHeight: 420 }}>
        {selected && request ? (
          <PickerPanel
            key={selectionId}
            day={selected.day}
            slot={selected.slot}
            request={request}
            onPicked={() => select(isEmpty(selected) ? firstEmptyDinner(selected.day) : selected)}
            extra={<SelectedMeal selection={selected} days={days} onMoved={(to) => select(to)} onRemoved={() => select(selected)} />}
          />
        ) : (
          <div style={{ border: `1px dashed ${COLORS.line}`, borderRadius: RADIUS.xl, padding: "var(--space-x3)", ...supporting }}>Click a meal in the week to choose a recipe or leftovers for it.</div>
        )}
      </div>
    </div>
  );
}

// One meal in a day column: its dishes, or "+ Dinner"; "Choosing…" while
// the panel is filling it. A planned meal can be dragged.
function MealCell({
  day,
  slot,
  lines,
  selected,
  dropping,
  onSelect,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  day: Day;
  slot: MealSlot;
  lines: DishLine[];
  selected: boolean;
  dropping: boolean;
  onSelect: () => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent) => void;
}) {
  const filled = lines.length > 0;
  const [main, ...others] = lines;
  const border = selected || dropping ? COLORS.accent : COLORS.line;
  const label = filled ? `${day} ${slot}: ${lines.map((l) => (l.isLeftover ? `leftovers of ${l.title}` : l.title)).join(", ")}` : `Add ${day} ${slot}`;
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label}
      draggable={filled}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ day, slot }));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onSelect}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: SPACE.hair,
        minHeight: slot === "dinner" ? 88 : 52,
        padding: "var(--space-sm) var(--space-md)",
        border: `${selected || dropping ? 2 : 1}px ${filled || selected ? "solid" : "dashed"} ${border}`,
        borderRadius: RADIUS.md,
        background: selected ? COLORS.accentTint : filled ? COLORS.card : "transparent",
        color: COLORS.ink,
        textAlign: "left",
        cursor: filled ? "grab" : "pointer",
        fontSize: FONT_SIZE.sm,
        overflowWrap: "anywhere",
      }}
    >
      {filled ? (
        <>
          <span style={{ ...smallLabel, fontSize: FONT_SIZE.xxs, color: main.isLeftover ? COLORS.caution : COLORS.soft }}>{main.isLeftover ? "Leftovers" : SLOT_TITLES[slot]}</span>
          <span style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: slot === "dinner" ? FONT_SIZE.base : FONT_SIZE.sm, lineHeight: 1.2 }}>{main.title}</span>
          {others.map((l) => (
            <span key={l.dishType} style={{ color: COLORS.soft, lineHeight: 1.2 }}>
              + {l.isLeftover ? `Leftovers: ${l.title}` : l.title}
            </span>
          ))}
          {main.isRepeat && <Flag tone="caution">Repeat</Flag>}
          {main.scaledTo && <Flag tone="accent">Scaled to {main.scaledTo}</Flag>}
          {main.cooked && <Flag tone="accent">✓ Cooked</Flag>}
        </>
      ) : selected ? (
        <span style={{ fontWeight: 700, color: COLORS.accent }}>Choosing…</span>
      ) : (
        <span style={{ fontWeight: 700, color: COLORS.accent }}>+ {SLOT_TITLES[slot]}</span>
      )}
    </button>
  );
}

function Flag({ tone, children }: { tone: "accent" | "caution"; children: ReactNode }) {
  const color = tone === "accent" ? COLORS.accent : COLORS.caution;
  return (
    <span style={{ alignSelf: "flex-start", marginTop: SPACE.xs, fontSize: FONT_SIZE.xxs, fontWeight: 700, color, border: `1px solid ${color}`, borderRadius: RADIUS.pill, padding: "0 var(--space-sm)" }}>
      {children}
    </span>
  );
}

// The selected meal's own actions, at the top of the panel: what it holds,
// remove a dish, and move the meal to another day (the keyboard's drag).
function SelectedMeal({ selection, days, onMoved, onRemoved }: { selection: Selection; days: Day[]; onMoved: (to: Selection) => void; onRemoved: () => void }) {
  const mp = useMealPlanner();
  const lines = dishLines(mp.plansByWeek, mp.weekStart, selection.day, selection.slot, mp.recipeById, new Date());
  if (lines.length === 0) return null;
  const remove = (line: DishLine) => {
    if (line.dishType === "entree") mp.setDish(selection.day, selection.slot, "entree", "");
    else mp.removeDishRow(selection.day, selection.slot, line.dishType);
    onRemoved();
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xs, marginTop: SPACE.md }}>
      {lines.map((line) => (
        <div key={line.dishType} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md, fontSize: FONT_SIZE.sm }}>
          <span style={{ minWidth: 0 }}>
            <span style={{ color: COLORS.soft }}>{line.label}: </span>
            {line.title}
          </span>
          {line.recipeId && !line.isLeftover && (
            <button type="button" aria-pressed={line.cooked} onClick={() => mp.setCooked(mp.weekStart, selection.day, selection.slot, line.dishType, !line.cooked)} style={{ ...textButton, minHeight: 32, fontSize: FONT_SIZE.sm, flexShrink: 0, color: line.cooked ? COLORS.accent : COLORS.soft }}>
              {line.cooked ? "✓ Cooked" : "Mark cooked"}
            </button>
          )}
          <button type="button" onClick={() => remove(line)} style={{ ...textButton, minHeight: 32, color: COLORS.danger, fontSize: FONT_SIZE.sm, flexShrink: 0 }}>
            Remove
          </button>
        </div>
      ))}
      <label style={{ ...supporting, display: "flex", alignItems: "center", gap: SPACE.md }}>
        Move to
        <select
          value=""
          onChange={(e) => {
            const day = e.target.value as Day;
            if (!(DAYS as readonly string[]).includes(day)) return;
            const to = { day, slot: selection.slot };
            mp.moveMeal(selection, to);
            onMoved(to);
          }}
          style={{ ...inputStyle, minHeight: 32, flex: 1 }}
        >
          <option value="">another day…</option>
          {days
            .filter((d) => d !== selection.day)
            .map((d) => (
              <option key={d} value={d}>
                {d}
                {Object.values(normalizeDishes(mp.weekData.days[d]?.[selection.slot]) as Record<string, string>).some(Boolean) ? " (swap)" : ""}
              </option>
            ))}
        </select>
      </label>
    </div>
  );
}
